import { useTranslation } from 'react-i18next';
import {
    HorizontalFlex,
    Input,
    PadContainer,
    SingleSelect,
    icons,
} from 'smartsuite-ui';
import { supportedCountriesEnglish } from '../constants/countries';
import { states } from '../constants/states';
import {
    isAddressValid,
    isAlpha,
    isAlphaSpaceHyphen,
    isZipCodeValid,
} from '../utils/validationUtil';

function AddressForm(props) {
    const { t } = useTranslation();
    const isNumberOfTagsValid =
        Number(props.data?.numberOfTags) > 0 &&
        (props?.isTotalNumberOfTagsValid ?? true);

    return (
        <fieldset className="address-form">
            <legend>
                <span
                    className={`address-form__legend ${
                        props?.disabled ? 'address-form__legend--muted' : ''
                    }`}
                >{`${t('address')} ${props.index + 1}`}</span>
            </legend>
            {props.index > 0 && !props.disabled && (
                <HorizontalFlex cssClassModifiers={['justify-flex-end']}>
                    <button
                        type="button"
                        className="btn btn--secondary btn--square"
                        align="right"
                        onClick={() => {
                            props.onClick(props.index);
                        }}
                    >
                        <span className={icons.trash} />
                    </button>
                </HorizontalFlex>
            )}
            <div className="field">
                <div className="clearable-input-container">
                    <label
                        className={`label label--small ${
                            props?.disabled ? 'label--disabled' : ''
                        }`}
                    >
                        {`${t('numberOfTags')} *`}
                    </label>
                    <input
                        id={`numberOfTags${props.index}`}
                        className={`input ${
                            isNumberOfTagsValid ? '' : 'input--error'
                        }`}
                        type="number"
                        onKeyDown={(event) => {
                            if (
                                !event.key.match(/\d+/) &&
                                !['Backspace', 'ArrowUp', 'ArrowDown'].includes(
                                    event.key
                                )
                            ) {
                                event.preventDefault();
                            }
                        }}
                        value={props.data?.numberOfTags}
                        onChange={(event) => {
                            if (
                                event.target.value.length <= 2 &&
                                Number(event.target.value) >= 0
                            )
                                props.onChange(
                                    'numberOfTags',
                                    event.target.value.replace(/^0+/, ''),
                                    props.index
                                );
                        }}
                        required
                        disabled={props?.disabled}
                    />
                    <span className="field__error" hidden={isNumberOfTagsValid}>
                        {props.isTotalNumberOfTagsValid
                            ? 'Must have at least 1 tag per address'
                            : 'Number of total tags exceeds limit'}
                    </span>
                </div>
                {!isNumberOfTagsValid && (
                    <PadContainer cssClassModifiers={['pad-small']} />
                )}
            </div>
            <Input
                label={`${t('recipientName')} *`}
                className="input"
                errorMessage={`${t('name')} ${t('inputInvalid')}`}
                id={'name'}
                isValid={isAlphaSpaceHyphen(props.data?.recipientName)}
                onChange={(event) => {
                    props.onChange(
                        'recipientName',
                        event.target.value,
                        props.index
                    );
                }}
                value={props.data?.recipientName}
                required
                disabled={props?.disabled}
            />
            <Input
                label={`${t('address')} ${t('line')} 1 *`}
                className="input"
                errorMessage={`${t('address')} ${t('inputInvalid')}`}
                id={'address'}
                isValid={
                    !props.data?.addressLine1 ||
                    isAddressValid(props.data?.addressLine1)
                }
                onChange={(event) => {
                    props.onChange(
                        'addressLine1',
                        event.target.value,
                        props.index
                    );
                }}
                value={props.data?.addressLine1}
                required
                disabled={props?.disabled}
            />
            <Input
                label={`${t('address')} ${t('line')} 2 (${t('optional')})`}
                className="input"
                errorMessage={`${t('address')} ${t('inputInvalid')}`}
                id={'address'}
                isValid={
                    !props.data?.addressLine2 ||
                    isAddressValid(props.data?.addressLine2)
                }
                onChange={(event) => {
                    props.onChange(
                        'addressLine2',
                        event.target.value,
                        props.index
                    );
                }}
                value={props.data?.addressLine2}
                disabled={props?.disabled}
            />
            <Input
                label={`${t('city')} *`}
                cssClassModifiers={['cityInput']}
                errorMessage={`${t('city')} ${t('inputInvalid')}`}
                id="city"
                isValid={isAlpha(props.data?.city)}
                onChange={(event) => {
                    props.onChange('city', event.target.value, props.index);
                }}
                value={props.data?.city}
                required
                disabled={props?.disabled}
            />
            <SingleSelect
                label={`${t('state')} *`}
                cssClassModifiers={['half-relative', 'stateInput']}
                id="state"
                isClearable={false}
                noOptionsMessage={() => t('noResults')}
                options={states}
                onChange={(event) => {
                    props.onChange('state', event, props.index);
                }}
                textField="textField"
                value={props.data.state}
                valueField="valueField"
                disabled={props?.disabled}
            />
            <Input
                label={`${t('postalCode')} *`}
                cssClassModifiers={['half-relative', 'postalCodeInput']}
                errorMessage={`${t('postalCode')} ${t('inputInvalid')}`}
                id="zipCode"
                isValid={
                    !props.data?.zipCode || isZipCodeValid(props.data?.zipCode)
                }
                maxLength={5}
                onChange={(event) => {
                    props.onChange('zipCode', event.target.value, props.index);
                }}
                value={props.data?.zipCode}
                required
                disabled={props?.disabled}
            />
            <SingleSelect
                label={`${t('country')} *`}
                cssClassModifiers={['half-relative', 'countrySelect']}
                id="country"
                isClearable={false}
                noOptionsMessage={() => t('noResults')}
                options={supportedCountriesEnglish}
                // onChange={(event) => {
                //     props.onChange('country', event, props.index);
                // }}
                textField="textField"
                value={props.data.country}
                valueField="valueField"
                disabled={true}
                autocomplete="off"
            />
        </fieldset>
    );
}

export default AddressForm;
